import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface CaptureContextValue {
  file: File | null;
  sourceMode: 'camera' | 'file';
  templateType: 'meeting' | 'task' | 'medical' | 'activity';
  activityPreset: 'golf' | null;
  setActivityPreset: (value: 'golf' | null) => void;
  setTemplateType: (value: 'meeting' | 'task' | 'medical' | 'activity') => void;
  setCapture: (file: File | null, sourceMode?: 'camera' | 'file') => void;
  clearCapture: () => void;
}

const CaptureContext = createContext<CaptureContextValue | null>(null);

export function CaptureProvider({ children }: { children: ReactNode }) {
  const [file, setFile] = useState<File | null>(null);
  const [sourceMode, setSourceMode] = useState<'camera' | 'file'>('file');
  const [templateType, setTemplateType] = useState<'meeting' | 'task' | 'medical' | 'activity'>('medical');
  const [activityPreset, setActivityPreset] = useState<'golf' | null>(null);
  const setCapture = useCallback((next: File | null, mode: 'camera' | 'file' = 'file') => { setFile(next); setSourceMode(mode); }, []);
  const clearCapture = useCallback(() => setFile(null), []);
  const value = useMemo(
    () => ({ file, sourceMode, templateType, activityPreset, setTemplateType, setActivityPreset, setCapture, clearCapture }),
    [file, sourceMode, templateType, activityPreset, setCapture, clearCapture],
  );
  return (
    <CaptureContext.Provider value={value}>
      {children}
    </CaptureContext.Provider>
  );
}

export function useCapture() {
  const context = useContext(CaptureContext);
  if (!context) throw new Error('useCapture must be used inside CaptureProvider');
  return context;
}
